import { useState } from 'react';
import { Product } from '@/store/Product/types';
import { fetchShopProductByBarCode } from '@/store/Product/useShopProductByBarCode';
import {
    fetchProductGlobalByBarCode,
    SearchProductGlobalByBarCodeResponse,
} from '@/store/Product/useSearchProductGlobalByBarCode';
import { getPlanogramsByStore } from '../../usePlanogramsByStore';
import { QuickAdjustPlanogram } from '../../types';

export type BarcodeLookupResult =
    | { kind: 'inPlanogram'; planogram: QuickAdjustPlanogram }
    | { kind: 'local'; product: Product }
    | { kind: 'global'; global: SearchProductGlobalByBarCodeResponse }
    | { kind: 'notFound' };

export const useBarcodeLookup = (storeId: number | undefined) => {
    const [isLookingUp, setIsLookingUp] = useState(false);

    const lookup = async (barcode: string): Promise<BarcodeLookupResult> => {
        setIsLookingUp(true);
        try {
            if (storeId) {
                const planograms = await getPlanogramsByStore(storeId, {
                    page: 1,
                    perPage: 20,
                    barcode,
                });
                const planogram = planograms.data.find((item) =>
                    item.product.BarCodes?.some(
                        (code) => code.code === barcode,
                    ),
                );
                if (planogram) return { kind: 'inPlanogram', planogram };
            }

            const product = await fetchShopProductByBarCode(barcode);
            if (product) return { kind: 'local', product };

            const global = await fetchProductGlobalByBarCode(barcode);
            if (global) return { kind: 'global', global };

            return { kind: 'notFound' };
        } finally {
            setIsLookingUp(false);
        }
    };

    const findPlanogramForProduct = async (
        product: Product,
    ): Promise<QuickAdjustPlanogram | undefined> => {
        if (!storeId) return undefined;

        setIsLookingUp(true);
        try {
            const barcodes = (product.BarCodes ?? [])
                .map((code) => code.code)
                .filter(Boolean);
            if (barcodes.length > 0) {
                for (const code of barcodes) {
                    // eslint-disable-next-line no-await-in-loop
                    const planograms = await getPlanogramsByStore(storeId, {
                        page: 1,
                        perPage: 20,
                        barcode: code,
                    });
                    const planogram = planograms.data.find(
                        (item) => item.product.id === product.id,
                    );
                    if (planogram) return planogram;
                }
                return undefined;
            }

            let page = 1;
            let lastPage = 1;
            do {
                // eslint-disable-next-line no-await-in-loop
                const planograms = await getPlanogramsByStore(storeId, {
                    page,
                    perPage: 20,
                    name: product.name,
                });
                const planogram = planograms.data.find(
                    (item) => item.product.id === product.id,
                );
                if (planogram) return planogram;
                lastPage = planograms.meta.last_page;
                page += 1;
            } while (page <= lastPage);
            return undefined;
        } finally {
            setIsLookingUp(false);
        }
    };

    return { lookup, findPlanogramForProduct, isLookingUp };
};
